import React from 'react';
import { motion } from 'framer-motion';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend, ReferenceLine } from 'recharts';
import { BarChart3, Clock, TrendingUp, DollarSign, Activity, Layers } from 'lucide-react';
// Mock Data
const depthData = Array.from({
  length: 40
}, (_, i) => {
  const price = 40 + i * 2;
  // Create a V shape for order book
  const bidVol = price < 80 ? Math.pow(80 - price, 1.5) * 2 : 0;
  const askVol = price > 80 ? Math.pow(price - 80, 1.5) * 2 : 0;
  return {
    price,
    bidVolume: -Math.round(bidVol),
    askVolume: Math.round(askVol),
    totalVolume: Math.round(bidVol + askVol)
  };
});
const volumeHistory = Array.from({
  length: 24
}, (_, i) => ({
  hour: `${i}:00`,
  volume: Math.round(Math.random() * 500 + 200 + (i > 8 && i < 20 ? 400 : 0)),
  spread: Math.random() * 5 + 1
}));
const CustomTooltip = ({
  active,
  payload,
  label
}: any) => {
  if (active && payload && payload.length) {
    return <div className="bg-white/95 backdrop-blur-xl border border-slate-200 rounded-xl p-3 shadow-xl z-50" data-id="element-2089">
        <p className="text-xs font-bold text-slate-900 mb-1" data-id="element-2090">Price: €{label}</p>
        {payload.map((entry: any) => <div key={entry.name} className="flex items-center gap-2 text-xs" data-id="element-2091">
            <div className="w-2 h-2 rounded-full" style={{
          backgroundColor: entry.color
        }} data-id="element-2092" />
            <span className="text-slate-600 capitalize" data-id="element-2093">{entry.name}:</span>
            <span className="font-mono font-medium" data-id="element-2094">
              {Math.abs(entry.value)} MW
            </span>
          </div>)}
      </div>;
  }
  return null;
};
export function IntradayLiquidityDashboard() {
  return <div className="min-h-screen p-6 space-y-6 pb-20 bg-teal-50/30" data-id="element-2095">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-2096">
        <div data-id="element-2097">
          <div className="flex items-center gap-2 mb-1" data-id="element-2098">
            <div className="p-2 bg-teal-500 rounded-lg" data-id="element-2099">
              <Layers size={20} className="text-white" data-id="element-2100" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight" data-id="element-2101">
              Intraday Liquidity
            </h1>
          </div>
          <p className="text-slate-500 text-sm md:text-base ml-11" data-id="element-2102">
            Order book depth, bid-ask spreads, and volume analysis
          </p>
        </div>

        <div className="flex items-center gap-4" data-id="element-2103">
          <div className="text-right" data-id="element-2104">
            <p className="text-xs text-slate-500" data-id="element-2105">Current Spread</p>
            <p className="text-lg font-bold text-teal-700" data-id="element-2106">€1.20</p>
          </div>
          <div className="text-right" data-id="element-2107">
            <p className="text-xs text-slate-500" data-id="element-2108">24h Volume</p>
            <p className="text-lg font-bold text-slate-900" data-id="element-2109">14.2 GWh</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-2110">
        {/* Order Book Depth */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="lg:col-span-2 bg-white/60 backdrop-blur-xl border border-slate-200/60 rounded-xl p-6 shadow-sm" data-id="element-2111">
          <div className="flex justify-between items-center mb-6" data-id="element-2112">
            <h3 className="text-lg font-bold text-slate-900" data-id="element-2113">
              Market Depth (XBID)
            </h3>
            <span className="text-xs font-mono bg-slate-100 px-2 py-1 rounded text-slate-600" data-id="element-2114">
              Mid Price: €80.00
            </span>
          </div>

          <div className="h-[400px] w-full" data-id="element-2115">
            <ResponsiveContainer width="100%" height="100%" data-id="element-2116">
              <AreaChart data={depthData} layout="vertical" margin={{
              top: 10,
              right: 30,
              left: 0,
              bottom: 0
            }} data-id="element-2117">
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" data-id="element-2118" />
                <XAxis type="number" hide data-id="element-2119" />
                <YAxis dataKey="price" type="number" domain={['dataMin', 'dataMax']} axisLine={false} tickLine={false} tick={{
                fontSize: 12,
                fill: '#64748b'
              }} unit=" €" width={50} data-id="element-2120" />
                <Tooltip content={<CustomTooltip data-id="element-2122" />} data-id="element-2121" />
                <ReferenceLine x={0} stroke="#94a3b8" data-id="element-2123" />
                <Area type="step" dataKey="bidVolume" name="Bids" stroke="#10b981" fill="#10b981" fillOpacity={0.3} data-id="element-2124" />
                <Area type="step" dataKey="askVolume" name="Asks" stroke="#ef4444" fill="#ef4444" fillOpacity={0.3} data-id="element-2125" />
                <Legend data-id="element-2126" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Volume & Spread Analysis */}
        <div className="space-y-6" data-id="element-2127">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="bg-white/60 backdrop-blur-xl border border-slate-200/60 rounded-xl p-6 shadow-sm h-full flex flex-col" data-id="element-2128">
            <h3 className="text-lg font-bold text-slate-900 mb-4" data-id="element-2129">
              Volume Profile
            </h3>
            <div className="flex-1" data-id="element-2130">
              <ResponsiveContainer width="100%" height="100%" minHeight={200} data-id="element-2131">
                <BarChart data={volumeHistory} data-id="element-2132">
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" data-id="element-2133" />
                  <XAxis dataKey="hour" hide data-id="element-2134" />
                  <Tooltip cursor={{
                  fill: 'rgba(0,0,0,0.05)'
                }} contentStyle={{
                  borderRadius: '8px',
                  border: 'none',
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                }} data-id="element-2135" />
                  <Bar dataKey="volume" fill="#0d9488" radius={[2, 2, 0, 0]} data-id="element-2136" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="mt-6" data-id="element-2137">
              <h4 className="text-sm font-bold text-slate-900 mb-2" data-id="element-2138">
                Liquidity Indicators
              </h4>
              <div className="space-y-3" data-id="element-2139">
                {[{
                label: 'Bid-Ask Spread',
                value: 'Tight',
                color: 'text-emerald-600',
                bg: 'bg-emerald-50'
              }, {
                label: 'Market Impact',
                value: 'Low',
                color: 'text-emerald-600',
                bg: 'bg-emerald-50'
              }, {
                label: 'Resilience',
                value: 'High',
                color: 'text-blue-600',
                bg: 'bg-blue-50'
              }].map((item, idx) => <div key={idx} className="flex justify-between items-center p-2 rounded-lg bg-white border border-slate-100" data-id="element-2140">
                    <span className="text-sm text-slate-600" data-id="element-2141">{item.label}</span>
                    <span className={`text-xs font-bold px-2 py-1 rounded ${item.color} ${item.bg}`} data-id="element-2142">
                      {item.value}
                    </span>
                  </div>)}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>;
}